/**
 * The in-list person panel's list logic, shared by every page that opens the
 * panel over a contacts table (Import Detail, the AI Review page).
 *
 * The URL is the state (`?person=&tab=`, see `usePersonPanelUrl`); this hook
 * adds what a LIST needs on top of it: the `‹ ›` stops in on-screen order, the
 * ROW the panel acts on, opening from an avatar and focus return on close.
 */
import type { ImportContactRow } from "@nu-assist/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type PersonTab, linkedinHandle } from "./personHandle";
import { type PersonPanelUrl, usePersonPanelUrl } from "./usePersonPanelUrl";

export interface PersonStop {
  handle: string;
  row: ImportContactRow;
}

export interface ListPersonPanel {
  personUrl: PersonPanelUrl;
  /** The open person, or null when closed OR the panel is not enabled. */
  openHandle: string | null;
  personStops: readonly PersonStop[];
  /** `openHandle`'s index in `personStops`, -1 when it is not on the page. */
  openIndex: number;
  openRow: ImportContactRow | undefined;
  openRowId: string | null;
  openPerson: (row: ImportContactRow, handle: string) => void;
  closePerson: () => void;
  stepPerson: (delta: number) => void;
}

export function useListPersonPanel({
  rows,
  enabled,
  openTab = "profile",
}: {
  /** The rows the table renders, in their on-screen order. */
  rows: readonly ImportContactRow[];
  /** The `person-panel` gate; when false a `?person=` link opens nothing. */
  enabled: boolean;
  /** The tab an avatar click opens on. */
  openTab?: PersonTab;
}): ListPersonPanel {
  const personUrl = usePersonPanelUrl();
  const openHandle = enabled ? personUrl.handle : null;
  // Rows without a LinkedIn profile have no handle and no avatar button, so
  // they are not stops on the ‹ › walk either. One stop per PERSON: a list can
  // hold the same profile twice (03 resolves such duplicates), and a second
  // stop with the same handle would be a URL the walk cannot move past.
  const personStops = useMemo(() => {
    const seen = new Set<string>();
    return rows.flatMap((row) => {
      const handle = linkedinHandle(row.linkedin_url);
      if (!handle || seen.has(handle)) return [];
      seen.add(handle);
      return [{ handle, row }];
    });
  }, [rows]);
  const openIndex = openHandle ? personStops.findIndex((p) => p.handle === openHandle) : -1;
  // The ROW the panel acts on. The URL names a person, but a page can list the
  // same profile twice with different states, so the row that was clicked (or
  // stepped onto) is kept here and wins while it is still on the page with that
  // handle. A deep link, or a row gone after a refetch, falls back to the
  // person's first row — the ‹ › stop.
  const [openContactId, setOpenContactId] = useState<string | null>(null);
  const openRow =
    (openHandle &&
      openContactId &&
      rows.find(
        (r) =>
          r.import_contact_id === openContactId && linkedinHandle(r.linkedin_url) === openHandle
      )) ||
    (openIndex >= 0 ? personStops[openIndex]?.row : undefined);
  // The avatar a panel opened from, for focus return when it closes.
  const personOpenerRef = useRef<HTMLElement | null>(null);

  const openPerson = useCallback(
    (row: ImportContactRow, handle: string) => {
      personOpenerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpenContactId(row.import_contact_id);
      // A panel already up (an avatar reached behind an overlay, say) SWITCHES
      // person: `move` replaces the entry. A second `open` would push a second
      // marked entry, and one close would then land on the previous person
      // instead of closing (/pr-review #954 iter 2, C N1).
      if (personUrl.handle) personUrl.move(handle);
      else personUrl.open(handle, openTab);
    },
    [personUrl, openTab]
  );
  const closePerson = useCallback(() => {
    personUrl.close();
  }, [personUrl]);
  // FOCUS RETURN ON ANY CLOSE — the close button, Escape, the scrim, and the
  // browser's Back, which never goes through `closePerson`. Keyed on the open
  // handle going away, after the commit that removed the drawer: the avatar of
  // the person on screen when it closed, else the one it opened from (a deep
  // link has neither).
  const lastOpenRef = useRef<{ handle: string; rowId: string | null } | null>(null);
  const openRowId = openRow?.import_contact_id ?? null;
  useEffect(() => {
    const was = lastOpenRef.current;
    lastOpenRef.current = openHandle ? { handle: openHandle, rowId: openRowId } : null;
    if (!was || openHandle) return;
    setOpenContactId(null);
    const avatar = `[data-person-avatar="${CSS.escape(was.handle)}"]`;
    const target =
      (was.rowId &&
        document.querySelector<HTMLElement>(
          `${avatar}[data-person-row="${CSS.escape(was.rowId)}"]`
        )) ||
      document.querySelector<HTMLElement>(avatar) ||
      personOpenerRef.current;
    target?.focus();
  }, [openHandle, openRowId]);
  const stepPerson = (delta: number) => {
    const next = personStops[openIndex + delta];
    if (!next) return;
    setOpenContactId(next.row.import_contact_id);
    personUrl.move(next.handle);
  };

  return {
    personUrl,
    openHandle,
    personStops,
    openIndex,
    openRow,
    openRowId,
    openPerson,
    closePerson,
    stepPerson,
  };
}
